import { useCallback } from 'react';

import { useAppStore } from '@/store';
import { refreshMonetization, useCredits } from '@/features/credits';
import submitWebHook, {
  FreeQuotaExceededError,
} from '@/requests/magic-flow/submitWebHook';
import createNotificationConfig from '@/utils/ui/createNotificationConfig';
import buildParamsFromInputs from '@/utils/flow/buildParamsFromInputs';
import { endOnboardingSession } from '@/utils/environment/onboardingSession';

import type { IAppStore } from '@/store';

export interface SubmitResult {
  id: string;
}

interface SubmitParams {
  flowId: string;
  params?: Record<string, unknown>;
  presetCreditCount?: number | null;
  preferFreeQuota?: boolean;
}

const selector = (store: IAppStore) => ({
  toolId: store.toolId,
  folderName: store.folderName,
  inputs: store.inputs,
  selectedPreset: store.selectedPreset,
});

/**
 * Owns how a submission gets paid for. `preferFreeQuota` runs it against the
 * free-quota webhook first; once that quota is spent the onboarding session ends
 * and the run is re-submitted as a paid one — the first run the user actually
 * pays for, so the first that needs credits. Resolves to `null` when they can't
 * cover it, with the offer screen already open, so callers only have to undo
 * their optimistic placeholder.
 */
const useGenerationSubmit = () => {
  const { toolId, folderName, inputs, selectedPreset } = useAppStore(selector);
  const { requireCredits } = useCredits();

  const submit = useCallback(
    async ({
      flowId,
      params: prebuiltParams,
      presetCreditCount,
      preferFreeQuota = false,
    }: SubmitParams): Promise<SubmitResult | null> => {
      const params = prebuiltParams ?? buildParamsFromInputs(inputs);
      const presetSlug = selectedPreset?.slug;
      const presetCategory = selectedPreset?.category;

      const post = async (useFreeQuota: boolean): Promise<SubmitResult> => {
        const { id } = await submitWebHook({
          webhookId: flowId,
          params,
          driveOptions: {
            folderName,
            packageId: toolId,
            // Backend workaround: the backend doesn't accept a separate `slug` parameter — adding an
            // extra parameter causes generations not to be saved into drive — so we smuggle the slug inside the
            // existing `category` field.
            ...(presetSlug && {
              attributes: {
                category: `slug:${presetSlug}, category:${
                  presetCategory || ''
                }`,
              },
            }),
          },
          notificationConfig: createNotificationConfig(),
          preferFreeQuota: useFreeQuota,
        });

        refreshMonetization();

        return { id };
      };

      try {
        return await post(preferFreeQuota);
      } catch (error) {
        if (!(error instanceof FreeQuotaExceededError)) throw error;

        endOnboardingSession();
        const hasCredits = await requireCredits({ flowId, presetCreditCount });

        return hasCredits ? post(false) : null;
      }
    },
    [inputs, folderName, toolId, selectedPreset, requireCredits]
  );

  return { submit };
};

export default useGenerationSubmit;
